/**
 * MainScorecardPage — Plan 05-12 Task 2.
 *
 * /main route: 3-tab SPA page (Sales Activity, Marketing, ROI) rendering ~45 tiles
 * from a single /api/main/scorecard payload via useMainScorecard().
 *
 * Per UI-SPEC §"Page-level layout (/main route)" + §"Tab structure":
 *   - AppShell chrome with brandSuffix="Main" + cross-link to CRO scorecard
 *   - Horizontal tab bar with URL deep-linking via ?tab=sales|marketing|roi
 *   - Per-tab tile sections dispatched via MAIN_TILE_MAPPING
 *   - ROI tab empty-state when Finance has not uploaded data
 *   - Campaign Metrics Phase-2 tiles in collapsed <details> section
 *
 * Inactive-tab snap queries are gated by activeTab === '<tab>' via TanStack Query
 * enabled flag (per UI-SPEC States Contract — lazy load).
 */
import { Link, useSearchParams } from 'react-router';

import { AppShell } from '../components/AppShell.js';
import { FreshnessPill } from '../components/FreshnessPill.js';
import { MainPlaceholderTile } from '../components/main/MainPlaceholderTile.js';
import { MainSummaryTab } from '../components/main/MainSummaryTab.js';
import { MainTabBar, type TabKey } from '../components/main/MainTabBar.js';
import { MainTaxonomyTab } from '../components/main/MainTaxonomyTab.js';
import { MainTeamTab } from '../components/main/MainTeamTab.js';
import { MainTileSection } from '../components/main/MainTileSection.js';
import { Button } from '../components/ui/Button.js';
import {
  HEADLINE_TILE_IDS,
  SALES_ACTIVITY_TILE_IDS,
  PIPELINE_TILE_IDS,
  EFFICIENCY_TILE_IDS,
  INBOUND_TILE_IDS,
  MKTPIPELINE_TILE_IDS,
  MKTANALYTICS_TILE_IDS,
  PHASE2_TILE_IDS,
  ROI_EFFICIENCY_TILE_IDS,
  ROI_CHANNEL_TILE_IDS,
  ROI_QUOTA_TILE_IDS,
  ALL_SALES_TILE_IDS,
  ALL_MARKETING_TILE_IDS,
  ALL_ROI_TILE_IDS,
} from '../lib/main-tile-mapping.js';
import { useCroMe } from '../queries/useCroMe.js';
import { useMainScorecard } from '../queries/useMainScorecard.js';

import type { FreshnessPill as FreshnessPillData } from '@saledash/shared';

const DEFAULT_FRESHNESS: FreshnessPillData = {
  status: 'live',
  lastRefreshedAt: new Date().toISOString(),
};

const ROI_EMPTY_TRIGGER = 'not uploaded';

export function MainScorecardPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey | null) ?? 'summary';

  const { data: scorecard, isLoading } = useMainScorecard();

  // Freshness from the first successful metric response
  const anyMetricFreshness =
    scorecard
      ? Object.values(scorecard.metrics)[0]?.freshness
      : undefined;

  const freshness: FreshnessPillData = anyMetricFreshness
    ? {
        status: 'live',
        lastRefreshedAt: anyMetricFreshness.lastRefreshedAt,
      }
    : DEFAULT_FRESHNESS;

  // ROI tab empty-state: check if MAIN-37 has "not uploaded" in placeholder_text
  const roi37 = scorecard?.metrics['MAIN-37'];
  const roiIsEmpty =
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- the optional-chain form yields undefined instead of false for this boolean
    roi37?.placeholder_text != null &&
    roi37.placeholder_text.toLowerCase().includes(ROI_EMPTY_TRIGGER);

  // Only the three data tabs carry a tile count; the Summary + Taxonomy reading
  // tabs render no count (MainTabBar hides it).
  const tileCounts: Partial<Record<TabKey, number>> = {
    sales: ALL_SALES_TILE_IDS.length,
    // Phase-2 + Marketing Analytics (MAIN-34/35/36) are collapsed/deferred —
    // don't count them as active marketing tiles (quick-260625-hz9 Task 14).
    marketing:
      ALL_MARKETING_TILE_IDS.length - PHASE2_TILE_IDS.length - MKTANALYTICS_TILE_IDS.length,
    roi: ALL_ROI_TILE_IDS.length,
    // Phase 6 Plan 01 — Team tab currently has one tile (pipeline-by-rep).
    // Subsequent slices (coverage, forecast-by-category, win-rate) increment this.
    team: 1,
  };

  function handleTabChange(tab: TabKey): void {
    setSearchParams({ tab });
  }

  // CRO cross-link visibility (2026-06-16 stakeholder feedback): only show
  // the "← CRO scorecard" affordance to users who actually have access to
  // the CRO report. Gate on useCroMe().isCroGroupMember.
  //
  // On apex (salesdashboard.adeptia.org) useCroMe is disabled (no Easy Auth),
  // so meData is undefined → link hidden. On cro.* the hook fires and we
  // read the group claim.
  //
  // When shown from the apex host we'd link to the cro subdomain absolute
  // URL (a relative '/' from apex would loop back through the host redirect
  // to /main). Since the link is hidden on apex anyway, the relative '/'
  // works for the only case we actually render it — cro.*/main back to
  // the CRO scorecard root.
  const { data: meData } = useCroMe();
  const showCroLink = meData?.isCroGroupMember === true;

  // Cross-link affordance (single AppShell slot): CRO members see the CRO
  // scorecard link; everyone else gets the /forecast cross-link so execs can
  // navigate /main ↔ /forecast (Phase 05.1 UI-SPEC).
  const crossLink = showCroLink
    ? { label: '← CRO scorecard', to: '/' }
    : { label: 'Forecast ↗', to: '/forecast' };

  return (
    <AppShell
      crossLink={crossLink}
      extraLinks={[{ label: '2027 Plan ↗', to: '/2027-plan' }]}
      freshnessSlot={<FreshnessPill freshness={freshness} />}
    >
      <div className="space-y-4">
        {/* Tab bar */}
        <MainTabBar
          activeTab={activeTab}
          onChange={handleTabChange}
          tileCounts={tileCounts}
        />

        {/* Tab panels */}
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          className="space-y-6"
        >
          {/* ── Sales Activity tab ──────────────────────────────── */}
          {activeTab === 'sales' && (
            <>
              {/* Headline band (quick-260626-cot Task 2.2) — Sales tab only.
                  4 hero metrics execs check first, moved out of their normal
                  sections so they appear once, here. */}
              <MainTileSection
                sectionKey="headline"
                label="HEADLINE"
                tileIds={HEADLINE_TILE_IDS}
                scorecardData={scorecard}
                isLoading={isLoading}
              />
              <MainTileSection
                sectionKey="activity"
                label="ACTIVITY"
                tileIds={SALES_ACTIVITY_TILE_IDS}
                scorecardData={scorecard}
                isLoading={isLoading}
              />
              <MainTileSection
                sectionKey="pipeline"
                label="PIPELINE"
                tileIds={PIPELINE_TILE_IDS}
                scorecardData={scorecard}
                isLoading={isLoading}
              />
              <MainTileSection
                sectionKey="efficiency"
                label="PIPELINE EFFICIENCY"
                tileIds={EFFICIENCY_TILE_IDS}
                scorecardData={scorecard}
                isLoading={isLoading}
              />
            </>
          )}

          {/* ── Marketing tab ──────────────────────────────────── */}
          {activeTab === 'marketing' && (
            <>
              <MainTileSection
                sectionKey="inbound"
                label="INBOUND LEADS"
                tileIds={INBOUND_TILE_IDS}
                scorecardData={scorecard}
                isLoading={isLoading}
              />
              <MainTileSection
                sectionKey="mktpipeline"
                label="MARKETING PIPELINE & EFFICIENCY"
                tileIds={MKTPIPELINE_TILE_IDS}
                scorecardData={scorecard}
                isLoading={isLoading}
              />

              {/* Marketing Analytics — deferred (collapsed section).
                  quick-260625-hz9 Task 14: MAIN-34/35/36 grouped here, mirroring
                  the Campaign Metrics Phase-2 details pattern. Compute unchanged. */}
              <details className="rounded-xl border border-ink-200 bg-white p-4">
                <summary className="cursor-pointer text-sm font-medium text-ink-600 select-none">
                  Marketing Analytics (deferred, {MKTANALYTICS_TILE_IDS.length} tiles)
                </summary>
                <div className="mt-3">
                  <MainTileSection
                    sectionKey="mktanalytics"
                    label="MARKETING ANALYTICS"
                    tileIds={MKTANALYTICS_TILE_IDS}
                    scorecardData={scorecard}
                    isLoading={isLoading}
                  />
                </div>
              </details>

              {/* Campaign Metrics — Phase 2 (collapsed section) */}
              <details className="rounded-xl border border-ink-200 bg-white p-4">
                <summary className="cursor-pointer text-sm font-medium text-ink-600 select-none">
                  Campaign Metrics — Phase 2 (deferred, 6 tiles)
                </summary>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {PHASE2_TILE_IDS.map((id) => (
                    <MainPlaceholderTile
                      key={id}
                      title={id}
                      primary="Deferred — Campaign Metrics Phase 2"
                      secondary="This tile activates once Adeptia's Marketing team configures Campaign Influence model per AUDIT-02."
                    />
                  ))}
                </div>
              </details>
            </>
          )}

          {/* ── ROI tab ────────────────────────────────────────── */}
          {activeTab === 'roi' && (
            roiIsEmpty ? (
              <RoiEmptyState />
            ) : (
              <>
                <MainTileSection
                  sectionKey="roi-efficiency"
                  label="EFFICIENCY"
                  tileIds={ROI_EFFICIENCY_TILE_IDS}
                  scorecardData={scorecard}
                  isLoading={isLoading}
                />
                <MainTileSection
                  sectionKey="roi-channel"
                  label="BY-CHANNEL ROI"
                  tileIds={ROI_CHANNEL_TILE_IDS}
                  scorecardData={scorecard}
                  isLoading={isLoading}
                />
                <MainTileSection
                  sectionKey="roi-quota"
                  label="QUOTA"
                  tileIds={ROI_QUOTA_TILE_IDS}
                  scorecardData={scorecard}
                  isLoading={isLoading}
                />
              </>
            )
          )}

          {/* ── Team tab (Sales-Manager pipeline-by-rep) ───────── */}
          {activeTab === 'team' && <MainTeamTab active />}

          {/* ── Summary tab (exec readout) ─────────────────────── */}
          {activeTab === 'summary' && (
            <MainSummaryTab onNavigateTab={handleTabChange} />
          )}

          {/* ── Taxonomy tab (metric glossary) ─────────────────── */}
          {activeTab === 'taxonomy' && <MainTaxonomyTab />}
        </div>
      </div>
    </AppShell>
  );
}

/**
 * ROI tab empty state — Finance has not uploaded data yet.
 * Per UI-SPEC §"ROI tab empty-state handling" + Copywriting Contract.
 */
function RoiEmptyState(): React.JSX.Element {
  return (
    <div
      className="py-16 text-center"
      data-testid="roi-empty-state"
    >
      <h2 className="text-lg font-semibold text-ink-900">No finance data yet</h2>
      <p className="mt-2 text-sm text-ink-500">
        Upload the templated monthly xlsx at /admin/finance-upload to populate these 8 tiles.
      </p>
      <div className="mt-6">
        <Link to="/admin/finance-upload">
          <Button variant="primary" size="md">
            Go to Finance Upload →
          </Button>
        </Link>
      </div>
    </div>
  );
}
